import React from 'react';
import _ from 'lodash';
import Alert from 'react-bootstrap/Alert';
import BsButton from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import ListGroup from 'react-bootstrap/ListGroup';

import { ReactComponent as UtilityFillWarning } from '@rss-eng/design-tokens/icons/utility-fill-warning.svg';
import {
  Button, Collection, ChoiceTiles, Modal,
} from '@janus.team/janus-particles';

import { ExternalLink } from 'common/ExternalLink';
import Drawer from 'common/drawer';
import { Loading } from 'components/loading/index';
import { useQueryModifyServices } from 'hooks/queries';
import { useRackspaceAccountContext } from 'customer/context';
import {
  getServiceBlockName, getCompatibleServiceBlocks, allServiceBlocks, serviceLevels, isBaseServiceBlock,
} from './utils';
import ErrorPanel from '../errorpanel';
import { proxyFetch } from '../fetch';

import './index.scss';

const calculateServiceBlocks = ({
  project,
  baseServiceBlocks,
  infrastructureModel,
  products,
}) => {
  let compatibleServiceBlockIds = [];
  if (infrastructureModel === 'no-resell') {
    compatibleServiceBlockIds = [...allServiceBlocks(products)];
  } else {
    compatibleServiceBlockIds = _(baseServiceBlocks)
      .map(_.partial(getCompatibleServiceBlocks, products))
      .flatten()
      .uniq()
      .value();
  }

  compatibleServiceBlockIds.sort();
  const serviceBlockIdsOnProject = _.map(project?.serviceBlocks, 'serviceBlockId');

  // Filter out base service blocks from project to avoid conflicts with billing config
  const nonBaseServiceBlockIdsOnProject = serviceBlockIdsOnProject.filter(
    (serviceBlockId) => !isBaseServiceBlock(serviceBlockId, products),
  );

  const serviceBlockIdsInitiallySet = _.uniq([
    ..._.intersection(compatibleServiceBlockIds, nonBaseServiceBlockIdsOnProject),
    ...baseServiceBlocks,
  ]);
  const serviceBlockIdsToDisplay = [...baseServiceBlocks, ..._.remove(compatibleServiceBlockIds, (sb) => !_.includes(baseServiceBlocks, sb))];

  return {
    serviceBlockIdsInitiallySet,
    serviceBlockIdsToDisplay,
  };
};

const calculateServiceConfiguration = ({
  productAccount, infrastructureModel, serviceModel, baseServiceBlocks, products,
}) => {
  if (serviceModel === 'standard') {
    // Standard model selections (service levels) are mutually exclusive
    return {
      servicesInitiallySet: _.includes(serviceLevels, productAccount.serviceLevel) ? [productAccount.serviceLevel] : [],
      servicesToDisplay: serviceLevels,
      requiredServices: [],
    };
  }
  const {
    serviceBlockIdsInitiallySet,
    serviceBlockIdsToDisplay,
  } = calculateServiceBlocks({
    project: productAccount,
    baseServiceBlocks,
    infrastructureModel,
    products,
  });
  return {
    servicesInitiallySet: serviceBlockIdsInitiallySet,
    servicesToDisplay: serviceBlockIdsToDisplay,
    requiredServices: infrastructureModel === 'no-resell' ? [] : baseServiceBlocks,
  };
};

const VerifyChangeServiceBlockModal = ({
  project, baseServiceBlocks, onSuccess, onClose, onError, infrastructureModel, products,
}) => {
  const { serviceBlockIdsInitiallySet, serviceBlockIdsToDisplay } = calculateServiceBlocks({
    project,
    baseServiceBlocks,
    infrastructureModel,
    products,
  });
  const [changing, setChanging] = React.useState({ changing: false, showModal: true });
  const [serviceBlocks, setServiceBlocks] = React.useState(serviceBlockIdsInitiallySet);
  const projectServiceBlocksUrl = (projectId) => `${process.env.REACT_APP_GCP_API}/projects/${projectId}/serviceBlocks`;
  const buildWarningMessage = (warning) => {
    if (!warning) return null;
    return { message: `Address issue with customer: ${_.get(warning, ['message'], 'unknown warning')}.` };
  };
  const changeServiceBlocksHandler = () => {
    setChanging((changing) => ({ ...changing, changing: true }));
    return proxyFetch(`${projectServiceBlocksUrl(project.projectId)}`, {
      method: 'PUT',
      headers: { 'X-Tenant-Id': project.rackspaceAccountNumber },
      body: { serviceBlocks: _.map(serviceBlocks, (serviceBlock) => { const block = { serviceBlockId: serviceBlock }; return block; }) },
    }, { useJanusLogin: true })
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.error) {
          throw _.get(resp, ['error', 'message'], JSON.stringify(resp.error));
        }
        onSuccess(buildWarningMessage(resp.warning));
      })
      .then(() => setChanging((changing) => ({ ...changing, changing: false, showModal: false })))
      .then(() => onClose())
      .catch((err) => {
        setChanging((changing) => ({ changing: false }));
        onError(err);
      });
  };
  const updateServiceBlocks = (id) => {
    const newServiceBlock = serviceBlocks.includes(id)
      ? _.without(serviceBlocks, id)
      : _.concat(serviceBlocks, id);
    return setServiceBlocks(newServiceBlock);
  };
  return (
    <>
      <Modal size="small" onClose={onClose}>
        <Modal.Header><h3 className="hx particles">Manage Service Blocks</h3></Modal.Header>
        <Modal.Body>
          { changing.changing
            ? <Loading centered />
            : (
              <ChoiceTiles name="serviceBlock" disabled={changing.changing}>
                {
                _.map(serviceBlockIdsToDisplay, (serviceBlock) => (
                  <ChoiceTiles.Tile
                    multi
                    key={serviceBlock}
                    value={serviceBlock}
                    checked={_.includes(serviceBlocks, serviceBlock) || _.includes(baseServiceBlocks, serviceBlock)}
                    onChange={() => updateServiceBlocks(serviceBlock)}
                    disabled={_.includes(baseServiceBlocks, serviceBlock)}
                  >
                    <ChoiceTiles.Header>{ getServiceBlockName(products, serviceBlock) }</ChoiceTiles.Header>
                  </ChoiceTiles.Tile>
                ))
              }
              </ChoiceTiles>
            )}
        </Modal.Body>
        <Modal.Footer>
          <Collection compact>
            <Button disabled={!!changing.changing} primary onClick={changeServiceBlocksHandler}>Update Service Blocks</Button>
            <Button disabled={!!changing.changing} secondary onClick={onClose}>Cancel</Button>
          </Collection>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const ManageServiceBlocksButton = ({
  project, onSuccess, baseServiceBlocks, infrastructureModel, products,
}) => {
  const [showModal, setShowModal] = React.useState(false);
  const [error, setError] = React.useState(null);
  if (_.isEmpty(products)) return null;
  if (error) return <ErrorPanel error={error} />;
  return (
    <>
      {showModal
        ? (
          <VerifyChangeServiceBlockModal
            project={project}
            baseServiceBlocks={baseServiceBlocks}
            onClose={() => setShowModal(false)}
            onError={setError}
            onSuccess={onSuccess}
            infrastructureModel={infrastructureModel}
            products={products}
          />
        )
        : null}
      <Button
        tertiary
        disabled={!!showModal || project?.linkedState !== 'LINKED'}
        onClick={() => setShowModal(true)}
      >
        Manage Service Blocks
      </Button>
    </>
  );
};

const CommonError = ({ code = 'UnknownError', message = '', context = {} }) => {
  const unknownError = {
    Title: () => <p>{code || 'Unknown Error'}</p>,
    Message: () => <small>{message}</small>,
  };
  const errorCodeComponents = {
    MissingPermissionsWarning: {
      Title: () => (
        <p>
          <UtilityFillWarning />
          {' '}
          Missing Permissions
          {' '}
          <UtilityFillWarning />
        </p>
      ),
      Message: () => (
        <>
          <p>The Rackspace automation service account is missing permissions on this project to support the enabled services.</p>
          {!_.isEmpty(context?.missing)
            && (
            <small>
              <ul>
                {_.map(context?.missing, (permission) => <li key={permission}>{permission}</li>)}
              </ul>
            </small>
            )}
          <small>
            Please work with the customer to remediate by ensuring the service account has been granted the required roles on the project.
            More details available in the
            <ExternalLink href="https://one.rackspace.com/x/a4ZMHw">Service Account Permissions wiki</ExternalLink>
          </small>
        </>
      ),
    },
    UnknownError: unknownError,
  };

  const ErrorComponent = _.get(errorCodeComponents, code, unknownError);

  return (
    <>
      <ErrorComponent.Title />
      <ErrorComponent.Message />
    </>
  );
};

export const ManageServicesDrawer = ({
  cloud, productAccount, showManageServices, setShowManageServices, products,
}) => {
  const { billing } = useRackspaceAccountContext();
  const { baseServiceBlocks, serviceModel, infrastructureModel } = _.get(billing, cloud, {});
  const { servicesInitiallySet, servicesToDisplay, requiredServices } = calculateServiceConfiguration({
    productAccount, baseServiceBlocks, serviceModel, infrastructureModel, products,
  });
  const [selectedServices, setSelectedServices] = React.useState(servicesInitiallySet);
  const updateServicesEnabled = React.useMemo(() => {
    const productAccountIsLegacy = serviceModel !== 'standard' && _.includes(serviceLevels, productAccount.serviceLevel);
    const hasSelectionChanges = !_.isEmpty(_.xor(servicesInitiallySet, selectedServices));
    const currentProjectServiceBlocks = _.map(productAccount?.serviceBlocks, 'serviceBlockId');
    const hasProjectServiceChanges = !_.isEqual(_.sortBy(selectedServices), _.sortBy(currentProjectServiceBlocks));

    // Enable when:
    // 1. User has made UI changes (selected/deselected services)
    // 2. Selected services differ from actual project state (base service block migration)
    // 3. Product account is legacy (always enable for legacy accounts)
    return hasSelectionChanges || hasProjectServiceChanges || productAccountIsLegacy;
  }, [servicesInitiallySet, selectedServices, serviceModel, productAccount.serviceLevel, productAccount?.serviceBlocks]);

  const mutateServices = useQueryModifyServices({ serviceModel });

  const closeDrawer = () => {
    mutateServices.reset();
    setSelectedServices(servicesInitiallySet);
    setShowManageServices(!showManageServices);
  };

  const updateSelectedServices = (id) => {
    // Standard model selections (service levels) are mutually exclusive
    if (serviceModel === 'standard') {
      return setSelectedServices([id]);
    }

    const newSelections = selectedServices.includes(id)
      ? _.without(selectedServices, id)
      : _.concat(selectedServices, id);

    return setSelectedServices(_.sortBy(newSelections));
  };

  const onUpdateServices = () => {
    const finalServiceBlocks = _.uniq([
      ...selectedServices,
      ...baseServiceBlocks, // Include base service block
    ]);
    mutateServices.mutate({ productAccount, selectedServices: finalServiceBlocks });
  };

  const SuccessAlert = () => {
    const hasWarning = !_.isEmpty(mutateServices.data.warning);
    const variant = hasWarning ? 'warning' : 'success';

    return (
      <Alert variant={variant} dismissible onClose={closeDrawer}>
        <p><strong>Services Updated</strong></p>
        {hasWarning && <CommonError {...mutateServices.data.warning} />}
      </Alert>
    );
  };

  const ServiceChoices = ({
    serviceModel, services, selectedServices, requiredServices = [], updateSelection,
  }) => (
    _.map(services, (service) => (
      <ListGroup.Item key={service} as="li" aria-label={`${getServiceBlockName(products, service)} service option`}>
        <div className="mpc-manage-services__listitem">
          <Form.Check
            value={service}
            id={service}
            name="service-options"
            type={serviceModel === 'standard' ? 'radio' : 'checkbox'}
            checked={_.includes(selectedServices, service) || _.includes(requiredServices, service)}
            onChange={() => updateSelection(service)}
            disabled={_.includes(requiredServices, service)}
          />
          <Form.Check.Label htmlFor={service}>
            <div className="mb-none text-truncate" title={getServiceBlockName(products, service)}>{getServiceBlockName(products, service)}</div>
            {_.includes(requiredServices, service)
                && (
                <small className="text-muted text-truncate">
                  Required service.
                  <br />
                  Please review the
                  {' '}
                  <ExternalLink href="https://one.rackspace.com/x/aVwTLQ">wiki</ExternalLink>
                  {' '}
                  if you need help changing this value.
                </small>
                )}
          </Form.Check.Label>
        </div>
      </ListGroup.Item>
    ))
  );

  return (
    <Drawer title="Manage Services" className="manage-services-drawer" backdrop placement="end" loading={false} handleClick={closeDrawer} show={showManageServices}>
      <section className="border-0 h-100">
        <div className="mpc-manage-services h-100">
          <div>
            <h4>{productAccount?.name}</h4>
            {mutateServices.isSuccess && <SuccessAlert />}
            {mutateServices.isError
              && (
              <Alert variant="error" dismissible onClose={mutateServices.reset}>
                <CommonError {...mutateServices.error} />
              </Alert>
              )}
          </div>
          <ListGroup>
            <ServiceChoices
              serviceModel={serviceModel}
              services={servicesToDisplay}
              selectedServices={selectedServices}
              requiredServices={requiredServices}
              updateSelection={updateSelectedServices}
            />
          </ListGroup>
          <div className="d-flex gap-4">
            <BsButton className="w-100" type="button" onClick={onUpdateServices} disabled={mutateServices.isLoading || !updateServicesEnabled}>
              {mutateServices.isLoading ? 'Updating Services…' : 'Update Services'}
            </BsButton>
            <BsButton className="w-100" variant="outline-primary" onClick={closeDrawer}>
              Cancel
            </BsButton>
          </div>
        </div>
      </section>
    </Drawer>
  );
};

export default ManageServiceBlocksButton;
